import PropTypes from "prop-types";
import React, { type ChangeEvent, useEffect, useRef, useState } from "react";

import {
	Button,
	Checkbox,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	SvgIcon,
	Typography,
	alpha,
	makeStyles,
	withStyles,
} from "@material-ui/core";
import Code from "@material-ui/icons/Code";
import Folder from "@material-ui/icons/Folder";
import TreeItem from "@material-ui/lab/TreeItem";
import TreeView from "@material-ui/lab/TreeView";
import { animated, useSpring } from "react-spring";
import { LazyLoading } from "../LazyLoading/LazyLoading";

export interface TreeNode {
	id: number;
	name: string;
	isCode: boolean;
	isActive: boolean;
	isInternal?: boolean;
	isSelectable?: boolean;
	isLeaf?: boolean;
	children?: TreeNode[];
	delayCategoryColor?: string;
}

interface ModalTreeFilterControlProps {
	onClose: (value?: number, name?: string, node?: TreeNode) => void;
	value?: number;
	open: boolean;
	title: string;
	data: TreeNode;
	selectBranch: boolean;
	selectActive: boolean;
	selectInternal: boolean;
	multipleSelectNodes: boolean;
	onHide?: (value: boolean) => void;
	listAssetDrawings?: number[];
	setListAssetDrawings?: React.Dispatch<React.SetStateAction<number[]>>;
	onSuccess?: (value: boolean) => void;
	isLoading: boolean;
}

function MinusSquare(props: any) {
	return (
		<SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
			<path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 11.023h-11.826q-.375 0-.669.281t-.294.682v0q0 .401.294 .682t.669.281h11.826q.375 0 .669-.281t.294-.682v0q0-.401-.294-.682t-.669-.281z" />
		</SvgIcon>
	);
}

function PlusSquare(props: any) {
	return (
		<SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
			<path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z" />
		</SvgIcon>
	);
}

function TransitionComponent(props: any) {
	const style = useSpring({
		from: { opacity: 0, transform: "translate3d(20px,0,0)" },
		to: {
			opacity: props.in ? 1 : 0,
			transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
		},
	});

	return (
		<animated.div style={style}>
			<Collapse {...props} />
		</animated.div>
	);
}

TransitionComponent.propTypes = {
	in: PropTypes.bool,
};

const StyledTreeItem = withStyles((theme) => ({
	iconContainer: {
		"& .close": {
			opacity: 0.3,
		},
	},
	group: {
		marginLeft: 7,
		paddingLeft: 18,
		borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
	},
	label: {
		backgroundColor: (props: any) =>
			props.isChecked ? "lightblue !important" : "inherit",
	},
}))((props: any) => (
	<TreeItem {...props} TransitionComponent={TransitionComponent} />
));

const useTreviewStyle = makeStyles((theme) => ({
	root: {
		minHeight: 264,
		flexGrow: 1,
	},
	treLabelIcon: {
		marginRight: theme.spacing(1),
	},
	treeLabelRoot: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(0.5, 0),
	},
	treeLabelText: {
		fontWeight: "inherit",
		flexGrow: 1,
	},
	treeLabelInactive: {
		textDecoration: "line-through",
		textDecorationColor: "red",
	},
	treeLabelUnselectable: {
		color: "grey",
	},
	smallCheckbox: {
		transform: "scale(0.8)",
	},
}));

const ModalTreeFilterControl: React.FC<ModalTreeFilterControlProps> = (
	props,
) => {
	const {
		onClose,
		value: valueProp,
		open,
		title,
		data,
		selectBranch,
		selectActive,
		selectInternal,
		multipleSelectNodes,
		onHide,
		listAssetDrawings,
		setListAssetDrawings,
		onSuccess,
		isLoading,
		...other
	} = props;

	const [value, setValue] = useState<number | undefined>(valueProp);
	const [expanded, setExpanded] = useState<string[]>([]);
	const treeRef = useRef<HTMLUListElement>(null);
	const [selectedNodes, setSelectedNodes] = useState<number[]>([]);

	const classes = useTreviewStyle();

	useEffect(() => {
		if (!open) {
			setValue(valueProp);
		} else {
			if (valueProp !== undefined) expandNode(valueProp);
		}
	}, [valueProp, open]);

	useEffect(() => {
		if (Array.isArray(listAssetDrawings) && listAssetDrawings.length >= 1) {
			setSelectedNodes(listAssetDrawings);
			const expandedBranches = findBranchesWithSelectedNodes(
				data,
				listAssetDrawings,
			);
			setExpanded(expandedBranches);
		}
	}, [listAssetDrawings, data]);

	const findBranchesWithSelectedNodes = (
		node: TreeNode,
		selectedNodes: number[],
	): string[] => {
		let branches: string[] = [];

		if (selectedNodes.includes(node.id)) {
			branches.push(node.id.toString());
		}

		if (Array.isArray(node.children)) {
			for (const child of node.children) {
				const childBranches = findBranchesWithSelectedNodes(
					child,
					selectedNodes,
				);
				if (childBranches.length > 0) {
					branches.push(node.id.toString());
					branches = branches.concat(childBranches);
				}
			}
		}

		return branches;
	};

	const handleCancel = () => {
		onClose();
		if (onHide) onHide(false);
	};

	const handleOk = () => {
		let findNode = getNodeData(data, value !== undefined ? value : -1);
		if (findNode === null) findNode = {} as TreeNode;
		var activeFilter =
			(selectActive && findNode.isActive) || selectActive !== true;
		var internalFilter =
			(selectInternal && findNode.isInternal) || selectInternal !== true;
		var branchFilter = selectBranch && !findNode.isLeaf;
		if (findNode.isLeaf) {
			if (activeFilter && internalFilter) {
				onClose(value, findNode.name, findNode);
				if (onSuccess) onSuccess(true);
			}
		} else {
			if (branchFilter) {
				onClose(value, findNode.name, findNode);
				if (onSuccess) onSuccess(true);
			}
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Escape") {
			onClose();
		}
	};

	const handleCheckboxClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
	};

	const getNodeParent = (
		node: TreeNode,
		id: number,
		chain: string[] = [],
	): string[] => {
		if (!node || node.id === undefined) {
			return [];
		}

		if (node.id === id) {
			chain.push(node.id.toString());
			return chain;
		}

		if (Array.isArray(node.children)) {
			chain.push(node.id.toString());
			for (let i = 0; i < node.children.length; i++) {
				const result = getNodeParent(node.children[i], id, chain.slice());
				if (result.length > 0) {
					return result;
				}
			}
		}

		return [];
	};

	const expandNode = (nodeId: number) => {
		const expandedNode = getNodeParent(data, nodeId, []);
		expandedNode.push(nodeId.toString());
		setExpanded(expandedNode);
	};

	const handleChange = (event: ChangeEvent<{}>, nodeIds: string) => {
		let findNode = getNodeData(
			data,
			nodeIds !== undefined ? Number.parseInt(nodeIds) : -1,
		);

		if (
			findNode?.hasOwnProperty("isSelectable") &&
			findNode.isSelectable === false
		) {
			return;
		}

		setValue(Number.parseInt(nodeIds));
	};

	const handleToogle = (event: ChangeEvent<{}>, nodeIds: string[]) => {
		setExpanded(nodeIds);
	};

	const getNodeData = (node: TreeNode, id: number): TreeNode | null => {
		if (id === undefined) return null;
		if (node.id === id) return node;
		if (Array.isArray(node.children)) {
			var i;
			var result = null;
			for (i = 0; result == null && i < node.children.length; i++) {
				result = getNodeData(node.children[i], id);
			}
			return result;
		}
		return null;
	};

	const handleCheckboxChange = (node: TreeNode) => {
		if (node) {
			const isSelected = selectedNodes.includes(node.id);
			let updatedSelectedNodes: number[];

			if (isSelected) {
				updatedSelectedNodes = selectedNodes.filter((id) => id !== node.id);
			} else {
				updatedSelectedNodes = [...selectedNodes, node.id];
			}

			setSelectedNodes(updatedSelectedNodes);

			if (setListAssetDrawings) {
				setListAssetDrawings(updatedSelectedNodes);
			}

			const expandedBranches = findBranchesWithSelectedNodes(
				data,
				updatedSelectedNodes,
			);
			setExpanded(expandedBranches);
		}
	};

	const renderTree = (nodes: TreeNode) => (
		<StyledTreeItem
			isChecked={selectedNodes.includes(nodes.id)}
			key={nodes.id}
			nodeId={nodes.id.toString()}
			label={
				<div className={classes.treeLabelRoot}>
					{nodes.isCode === true && (
						<Code className={classes.treLabelIcon} htmlColor={"#1ABC9C"} />
					)}
					{nodes.isCode === false && (
						<Folder className={classes.treLabelIcon} htmlColor={"#F1C40F"} />
					)}
					<Typography
						variant="body2"
						className={`
							${classes.treeLabelText}
							${!nodes.isActive ? classes.treeLabelInactive : ""}
							${
								nodes?.hasOwnProperty("isSelectable") &&
								nodes.isSelectable === false
									? classes.treeLabelUnselectable
									: ""
							}
						`}
					>
						<span>
							{nodes.isCode && (
								<i
									className="fas fa-square mr-2"
									style={{ color: nodes.delayCategoryColor || "#FFFFFF" }}
								></i>
							)}
							{nodes.name}
						</span>
					</Typography>
					{multipleSelectNodes && (
						<Checkbox
							className={classes.smallCheckbox}
							size="small"
							color="primary"
							checked={selectedNodes.includes(nodes.id)}
							onChange={() => {
								handleCheckboxChange(nodes);
							}}
							onClick={handleCheckboxClick}
						/>
					)}
				</div>
			}
			className={"delay-item-node"}
			id={nodes.id.toString()}
			data-isbranch={!nodes.isCode}
		>
			{Array.isArray(nodes.children)
				? nodes.children.map((node: TreeNode) => renderTree(node))
				: null}
		</StyledTreeItem>
	);

	return (
		<Dialog
			onClose={onHide ? () => onHide(false) : () => null}
			aria-label={title}
			maxWidth="sm"
			disableAutoFocus
			disableEnforceFocus
			disableRestoreFocus
			aria-labelledby="confirmation-dialog-title"
			open={open}
			{...other}
			onKeyDown={handleKeyDown}
		>
			<DialogTitle id="confirmation-dialog-title">{title}</DialogTitle>
			{isLoading ? (
				<div
					style={{
						width: 600,
						minHeight: 416,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<LazyLoading />
				</div>
			) : (
				<DialogContent dividers>
					<TreeView
						autoFocus={false}
						className={classes.root}
						defaultCollapseIcon={<MinusSquare />}
						defaultExpandIcon={<PlusSquare />}
						ref={treeRef}
						selected={value === undefined ? "" : value.toString()}
						expanded={expanded}
						onNodeSelect={handleChange}
						onNodeToggle={handleToogle}
					>
						{data && data.id !== undefined && renderTree(data)}
					</TreeView>
				</DialogContent>
			)}
			<DialogActions>
				<Button onClick={handleCancel} color="primary">
					Cancel
				</Button>
				<Button onClick={handleOk} color="primary">
					OK
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export { ModalTreeFilterControl };
